import { useEffect, useState } from 'react';
import api from '../../../api/api';
import { NotificationDoc, NotificationsResult } from '../../../model/NotificationsResult';

// Module-level store (same idea as Toast) so the header bell and the Notifications page share one list and read state.
// Loaded with the header (page load / client switch) and again when the bell or page opens — no polling.

// must match the NotificationCategory* values in investi.Core/Util/Constants.cs (not enforced)
export type NotificationCategory ='approvals' | 'questions' | 'registry' | 'email' | 'hub';
export type NotificationGroup = 'today' | 'yesterday' | 'week' | 'earlier';

export interface NotificationItem {
    id: string;
    category: NotificationCategory;
    group: NotificationGroup;
    title: string;
    // full text for the page; short = the dropdown's shorter wording
    body: string;
    short: string;
    // relative time for the page / the dropdown
    time: string;
    shortTime: string;
    action: string;
    route: string;
    created: string;
    unread: boolean;
    danger?: boolean;
}

export interface NotificationsState {
    stockId: string | null;
    items: NotificationItem[];
    unreadCount: number;
    hasOlder: boolean;
}

const PAGE_SIZE = 50;
let state: NotificationsState = { stockId: null, items: [], unreadCount: 0, hasOlder: false };
let listeners: ((s: NotificationsState) => void)[] = [];
const emit = (next: NotificationsState) => { state = next; listeners.forEach(l => l(state)); };

const ROUTES: Record<string, (sourceId: string) => string> = {
    NewQuestion: guid => `/communications/investor-questions/${guid}`,
    ApprovalNeeded: sendGuid => `/communications/announcement/${sendGuid}`,
    RegistryUpdated: date => `/share-registry/holdings-movement/${date}`,
    NewInvestorUser: () => '/share-registry',
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const clock = (d: Date) => d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
const dayMonth = (d: Date) => d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

const getGroup = (d: Date, now: Date): NotificationGroup => {
    const days = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return 'week';
    return 'earlier';
};

const getTimes = (d: Date, now: Date, group: NotificationGroup) => {
    if (group === 'today') {
        const mins = Math.floor((now.getTime() - d.getTime()) / 60000);
        const time = mins < 1 ? 'Just now' : mins < 60 ? `${mins} min ago` : `${Math.floor(mins / 60)} h ago`;
        return { time, shortTime: time };
    }
    if (group === 'yesterday') {
        return { time: `Yesterday, ${clock(d)}`, shortTime: 'Yesterday' };
    }
    return { time: dayMonth(d), shortTime: d.toLocaleDateString('en-AU', { weekday: 'short' }) };
};

const getItem = (n: NotificationDoc, r: NotificationsResult, now: Date): NotificationItem => {
    const d = new Date(n.created);
    const group = getGroup(d, now);
    const route = ROUTES[n.type];
    return {
        id: n.id,
        created: n.created,
        category: n.category as NotificationCategory,
        group,
        title: n.title,
        body: n.detail,
        short: n.short,
        ...getTimes(d, now, group),
        action: n.action,
        route: route ? route(n.sourceId) : '/notifications',
        unread: d > new Date(r.readUpTo) && !r.readIds.includes(n.id),
    };
};

export const loadNotifications = async (stockId: string) => {
    const r = await api().getNotifications(stockId);
    const now = new Date();
    emit({
        stockId,
        items: r.items.map(n => getItem(n, r, now)),
        unreadCount: r.unreadCount,
        hasOlder: r.items.length === PAGE_SIZE,
    });
};

export const loadOlderNotifications = async () => {
    const { stockId, items } = state;
    if (!stockId || items.length === 0) return;
    const r = await api().getNotifications(stockId, items[items.length - 1].created);
    const now = new Date();
    emit({
        ...state,
        items: [...items, ...r.items.map(n => getItem(n, r, now))],
        hasOlder: r.items.length === PAGE_SIZE,
    });
};

export const setRead = (id: string) => {
    const item = state.items.find(x => x.id === id);
    if (!state.stockId || !item?.unread) return;
    emit({ ...state, items: state.items.map(x => x.id === id ? { ...x, unread: false } : x), unreadCount: Math.max(0, state.unreadCount - 1) });
    api().readNotifications(state.stockId, [id]);
};

export const markAllRead = () => {
    if (!state.stockId) return;
    emit({ ...state, items: state.items.map(x => ({ ...x, unread: false })), unreadCount: 0 });
    api().readAllNotifications(state.stockId);
};

export const useNotifications = () => {
    const [current, setCurrent] = useState<NotificationsState>(state);
    useEffect(() => {
        listeners.push(setCurrent);
        return () => { listeners = listeners.filter(l => l !== setCurrent); };
    }, []);
    return current;
};
